import { useMemo, useEffect } from "react";
import { Button, DateRangePicker, DateValue, RangeValue } from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { parseDate } from "@internationalized/date";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";

export type DateRangePickerWithPresetsProps = {
    value: RangeValue<DateValue> | null;
    onChange: (value: RangeValue<DateValue> | null) => void;
    label?: string;
    className?: string;
    size?: "sm" | "md" | "lg";
    ariaLabel?: string;
};

function DateRangePickerWithPresets({
    value,
    onChange,
    label,
    className,
    size = "sm",
    ariaLabel = "Date Range Picker",
}: DateRangePickerWithPresetsProps) {
    const { t } = useTranslation("insights");
    const { selectedCropCycle } = useFieldDashboard() ?? {};

    const lastTwoMonths = useMemo(() => {
        if (!value?.start || !value?.end) return false;
        return dayjs(value.start.toString())
            .add(2, "months")
            .isSame(dayjs(value.end.toString()), "days");
    }, [value]);

    const lastTwoWeeks = useMemo(() => {
        if (!value?.start || !value?.end) return false;
        return dayjs(value.start.toString())
            .add(2, "weeks")
            .isSame(dayjs(value.end.toString()), "days");
    }, [value]);

    useEffect(() => {
        if (!selectedCropCycle) return;
        const today = getTodayWithTimeTravel();
        const cycleEnd = dayjs(selectedCropCycle.end_date);
        if (cycleEnd.isBefore(today)) {
            onChange({
                start: parseDate(
                    dayjs(selectedCropCycle.start_date).format("YYYY-MM-DD"),
                ),
                end: parseDate(cycleEnd.format("YYYY-MM-DD")),
            });
        } else {
            onChange({
                start: parseDate(
                    today.subtract(1, "months").format("YYYY-MM-DD"),
                ),
                end: parseDate(today.format("YYYY-MM-DD")),
            });
        }
    }, [selectedCropCycle, onChange]);

    return (
        <div className={className ?? "flex flex-row gap-2 w-full items-end "}>
            <DateRangePicker
                value={value}
                onChange={onChange}
                label={label ?? t("dateRange")}
                className="max-w-xs"
                size={size}
                aria-label={ariaLabel}
                labelPlacement="outside"
            />
            <Button
                color={lastTwoMonths ? "primary" : "default"}
                variant="flat"
                size={size}
                onPress={() => {
                    onChange({
                        start: parseDate(
                            dayjs(getTodayWithTimeTravel())
                                .subtract(2, "months")
                                .format("YYYY-MM-DD"),
                        ),
                        end: parseDate(
                            dayjs(getTodayWithTimeTravel()).format(
                                "YYYY-MM-DD",
                            ),
                        ),
                    });
                }}
            >
                {lastTwoMonths && <FontAwesomeIcon icon={faCheck} />}
                {t("lastTwoMonths")}
            </Button>
            <Button
                variant="flat"
                color={lastTwoWeeks ? "primary" : "default"}
                size={size}
                onPress={() => {
                    onChange({
                        start: parseDate(
                            dayjs(getTodayWithTimeTravel())
                                .subtract(2, "weeks")
                                .format("YYYY-MM-DD"),
                        ),
                        end: parseDate(
                            dayjs(getTodayWithTimeTravel()).format(
                                "YYYY-MM-DD",
                            ),
                        ),
                    });
                }}
            >
                {lastTwoWeeks && <FontAwesomeIcon icon={faCheck} />}
                {t("lastTwoWeeks")}
            </Button>
        </div>
    );
}

export default DateRangePickerWithPresets;
